import { isPlatformBrowser } from "@angular/common";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject } from "rxjs";

const STORAGE_KEY = 'app_locale';

@Injectable({
    providedIn: 'root'
})
export class LocaleService {
    private _locale = new BehaviorSubject<string>('da');
    readonly locale$ = this._locale.asObservable();

    private nfCache = new Map<string, Intl.NumberFormat>();
    private dfCache = new Map<string, Intl.DateTimeFormat>();

    constructor(
        private translate: TranslateService,
        @Inject(PLATFORM_ID) private platformId: Object
    ) {
        const isBrowser = isPlatformBrowser(this.platformId);

        const savedLocale = isBrowser ? (localStorage.getItem(STORAGE_KEY) || '').toLowerCase() : '';
        const initialLocale = savedLocale || this.translate.getCurrentLang() || this.translate.getFallbackLang() || 'da';

        if (this.translate.getCurrentLang() !== initialLocale) {
            this.translate.use(initialLocale);
        }
        this._locale.next(initialLocale);

        this.translate.onLangChange.subscribe(e => {
            const lang = (e.lang || 'da').toLowerCase();
            this._locale.next(lang);
            if (isBrowser) {
                localStorage.setItem(STORAGE_KEY, lang);
            }
        });
    }

    setLocale(lang: 'en' | 'da' | string) {
        this.translate.use(lang);
    }

    get currentLocale(): string {
        return this._locale.value;
    }

    /**
     * @param value - The number to format
     * @param digits - The digit info string in the format "minInt.minFrac-maxFrac".
     *                 minInt: minimum integer digits (default 1),
     *                 minFrac: minimum fraction digits (default 1),
     *                 maxFrac: maximum fraction digits (default 2)
     * @param useGrouping - Whether to use grouping separators (default: true)
     * @returns The formatted number string, or empty string if value is not finite'
     */
    formatNumber(value: number, digits: string = '1.1-2', useGrouping: boolean = true):string {
        if (!Number.isFinite(value)) return '';

        const { minInt, minFrac, maxFrac } = this.parseDigits(digits);
        const locale = this.resolveLocale(this.currentLocale);

        const key = `${locale}|${minInt}|${minFrac}|${maxFrac}|${useGrouping}`;
        let nf = this.nfCache.get(key);
        if (!nf) {
            nf = new Intl.NumberFormat(locale, {
                minimumIntegerDigits: minInt,
                minimumFractionDigits: minFrac,
                maximumFractionDigits: maxFrac,
                useGrouping
            });
            this.nfCache.set(key, nf);
        }
        return nf.format(value);
    }

    /**
     * @param value - The date value to format (string or Date)
     * @param options - Optional Intl.DateTimeFormatOptions to customize the formatting
     * @returns The formatted date string, or empty string if value is invalid
     */
    formatDate(value: string | Date, options?: Intl.DateTimeFormatOptions): string {
        if (!value) return '';

        const date = typeof value === 'string' ? new Date(value) : value;
        if (isNaN(date.getTime())) return '';

        const locale = this.resolveLocale(this.currentLocale);

        const key = `${locale}|${JSON.stringify(options || {})}`;
        let df = this.dfCache.get(key);
        if (!df) {
            df = new Intl.DateTimeFormat(locale, options || {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
            this.dfCache.set(key, df);
        }
        return df.format(date);
    }

    private resolveLocale(l?: string): string {
        const lc = (l || '').toLowerCase();
        if (lc.startsWith('da')) return 'da-DK'; // 1.234.567,89
        if (lc.startsWith('en')) return 'en-US'; // 1,234,567.89
        return 'en-US';
    }

    private parseDigits(d: string) {
        const m = /^(\d+)?\.(\d+)(?:-(\d+))?$/.exec((d || '').trim());
        const minInt = m?.[1] ? parseInt(m[1], 10) : 1;
        const minFrac = m?.[2] ? parseInt(m[2], 10) : 0;
        const maxFrac = m?.[3] ? parseInt(m[3], 10) : minFrac;
        return { minInt, minFrac, maxFrac };
    }
}